import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Ticket, TrendingUp, Activity, ChevronRight, ListChecks, Film, Crown, QrCode, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global, sticky "pulse" bar shown on every Coach tab, right under the header.
// Each metric is tappable and jumps the coach straight to where the work lives.
export default function BusinessPulseBar({ token, onJump, refreshKey }) {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/coach/business-pulse`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const b = data?.engagement_breakdown;
  const engTip = b
    ? `${b.proofs} video proofs · ${b.chapters} chapters · ${b.installs} installs · ${b.move_views} move views — tap to see players by last active`
    : "Tap to see players sorted by last active";
  const pending = data?.pending_reviews ?? 0;
  const hf = data?.hero_film;
  const qr = data?.qr_scans;

  const rev = data?.revenue;
  const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;

  const tiles = [
    {
      key: "revenue",
      testid: "pulse-revenue",
      label: "Revenue",
      value: money(rev?.total),
      sub: `${money(rev?.prelaunch?.total)} pre-launch · ${money(rev?.post_launch?.total)} since Oct 1`,
      icon: DollarSign,
      accent: "#D4AF37",
      onClick: () => onJump("content", { scroll: "blueprint-members" }),
      tip: `Paid Stripe revenue, test accounts excluded. ${rev?.prelaunch?.count ?? 0} pre-launch Blueprint sales (${money(rev?.prelaunch?.total)}) + ${rev?.post_launch?.count ?? 0} since launch — tap for Blueprint Members`,
    },
    {
      key: "trials",
      testid: "pulse-active-trials",
      label: "Active Trials",
      value: data?.active_trials ?? 0,
      sub: "on Elite trial now",
      icon: Ticket,
      accent: "#D4AF37",
      onClick: () => onJump("trials"),
      tip: "Tap to open Trial Passes",
    },
    {
      key: "engagement",
      testid: "pulse-engagement",
      label: "Engagement",
      value: (data?.engagement ?? 0).toLocaleString(),
      sub: "weighted activity score",
      icon: Activity,
      accent: "#C41E24",
      onClick: () => onJump("players", { sort: "last_active" }),
      tip: engTip,
    },
    {
      key: "reviews",
      testid: "pulse-pending-reviews",
      label: "Pending Reviews",
      value: pending,
      sub: pending > 0 ? "players waiting on you" : "queue is clear",
      icon: ListChecks,
      accent: pending > 0 ? "#C41E24" : "#22C55E",
      dot: pending > 0,
      onClick: () => onJump("players", { filter: "needs_review" }),
      tip: "Tap to see players flagged Needs Review",
    },
    {
      key: "conversions",
      testid: "pulse-conversions",
      label: "Conversions",
      value: data?.conversions ?? 0,
      sub: `${data?.conversion_rate ?? 0}% conversion rate`,
      icon: TrendingUp,
      accent: "#22C55E",
      onClick: () => onJump("trials", { filter: "converted" }),
      tip: "Tap to see trial passes that converted",
    },
    {
      key: "hero",
      testid: "pulse-hero-film",
      label: "Hero Film",
      value: hf?.sound_on ?? 0,
      sub: `${hf?.views ?? 0} views · ${hf?.completion_rate ?? 0}% completion · 7d`,
      icon: Film,
      accent: "#D4AF37",
      onClick: () => onJump("content", { scroll: "ad-attribution" }),
      tip: `Sound-on plays of the landing hero film (last 7 days). ${hf?.views ?? 0} views, ${hf?.completes ?? 0} watched to the end — tap for Ad Attribution`,
    },
    {
      key: "qr",
      testid: "pulse-guide-scans",
      label: "Guide Scans",
      value: qr?.week ?? 0,
      sub: `${qr?.today ?? 0} today · ${qr?.events?.[0] ? `top: ${qr.events[0].content}` : "Foundation Guide QR · 7d"}`,
      icon: QrCode,
      accent: "#C41E24",
      onClick: () => onJump("content", { scroll: "mk-event-guides" }),
      tip: `Foundation Guide QR scans this week (${qr?.total ?? 0} all-time). Tap to manage per-event guides`,
    },
  ];
  const top = data?.top_source;

  return (
    <div data-testid="business-pulse-bar" className="sm:sticky sm:top-[68px] z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2.5 bg-[#0d0d0d]/92 backdrop-blur-xl border-b border-white/10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2 mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45">Business Pulse · All Clubs</span>
        </div>
        <button data-testid="pulse-top-source" onClick={() => onJump("content", { scroll: "ad-attribution" })} title="Source with the most paid sales in the last 7 days — tap for Ad Attribution"
          className="inline-flex items-center gap-1.5 max-w-full min-h-[44px] sm:min-h-0 rounded-full border px-3 sm:px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors hover:brightness-110"
          style={{ borderColor: "rgba(212,175,55,0.5)", background: "rgba(212,175,55,0.12)", color: "#D4AF37" }}>
          <Crown size={11} />
          <span className="hidden sm:inline text-white/55">Top converting source · 7d</span>
          <span data-testid="pulse-top-source-value" className="truncate">{top ? `${top.source} · ${top.sales} sale${top.sales === 1 ? "" : "s"} · $${Math.round(top.revenue)}` : "No paid sales yet"}</span>
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-2">
        {tiles.map((t) => (
          <button
            key={t.key}
            data-testid={t.testid}
            onClick={t.onClick}
            title={t.tip}
            className="group text-left bg-[#141414] hover:bg-[#191919] border border-white/10 hover:border-white/25 rounded-lg px-3 py-2 min-h-[44px] transition-colors"
            style={{ borderLeftColor: t.accent, borderLeftWidth: 3 }}
          >
            <div className="flex items-center justify-between mb-0.5">
              <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-white/50">
                {t.dot && <span data-testid="pulse-review-dot" className="w-1.5 h-1.5 rounded-full bg-[#C41E24]" />}
                {t.label}
              </span>
              <t.icon size={13} style={{ color: t.accent }} />
            </div>
            <div className="flex items-end gap-1.5">
              <span className="font-cover text-3xl leading-none text-white" data-testid={`${t.testid}-value`}>{t.value}</span>
              <ChevronRight size={14} className="text-white/25 group-hover:text-white/60 transition-colors mb-0.5" />
            </div>
            <div className="hidden sm:block text-[10px] text-white/40 mt-0.5 truncate">{t.sub}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
